// Audio capture — HANDOFF schema Section 9.
// A thin class wrapping getUserMedia + MediaRecorder. One instance per
// recording (TextRecordingScreen and ImageRecordingScreen each create
// their own, then tear it down on unmount).

const MIME_TYPE_PREFERENCE = [
  'audio/webm;codecs=opus',
  'audio/ogg;codecs=opus',
  'audio/mp4;codecs=mp4a',
  'audio/webm',
];

export function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return null;
  return MIME_TYPE_PREFERENCE.find((t) => MediaRecorder.isTypeSupported(t)) ?? null;
}

export class AudioRecorder {
  constructor() {
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
    this.mimeType = null;
    this.constraintsRequested = null;
    this.constraintsActual = null;
    this._stopResolve = null;
  }

  /** Requests the mic and starts recording. Throws if permission is denied. */
  async start() {
    const constraints = {
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
        sampleRate: { ideal: 48000 },
        channelCount: { ideal: 1 },
      },
    };
    this.constraintsRequested = {
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      sampleRate: 48000,
      channelCount: 1,
    };

    this.stream = await navigator.mediaDevices.getUserMedia(constraints);

    // Read back what the browser/OS actually honored — this is stored in
    // device_info.audio_constraints_actual so the researcher can see
    // whether e.g. noiseSuppression:false was really applied.
    const track = this.stream.getAudioTracks()[0];
    const settings = track.getSettings();
    this.constraintsActual = {
      echoCancellation: settings.echoCancellation ?? null,
      noiseSuppression: settings.noiseSuppression ?? null,
      autoGainControl: settings.autoGainControl ?? null,
      sampleRate: settings.sampleRate ?? null,
      channelCount: settings.channelCount ?? null,
    };

    this.mimeType = pickMimeType();
    if (!this.mimeType) {
      this.stream.getTracks().forEach((t) => t.stop());
      throw new Error('No supported audio recording format on this device.');
    }

    this.chunks = [];
    this.recorder = new MediaRecorder(this.stream, {
      mimeType: this.mimeType,
      audioBitsPerSecond: 128000,
    });
    this.recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };

    // 1s timeslice protects against data loss if the tab is killed mid-recording.
    this.recorder.start(1000);
    this.startedAt = new Date().toISOString();
  }

  /** Stops recording and resolves with the assembled Blob. */
  stop() {
    return new Promise((resolve) => {
      if (!this.recorder || this.recorder.state === 'inactive') {
        resolve(new Blob(this.chunks, { type: this.mimeType ?? 'audio/webm' }));
        return;
      }
      this.recorder.onstop = () => {
        const blob = new Blob(this.chunks, { type: this.mimeType });
        this._releaseStream();
        resolve(blob);
      };
      this.recorder.stop();
      this.endedAt = new Date().toISOString();
    });
  }

  /** Stops and discards — used on session abandonment. */
  abort() {
    try {
      if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop();
    } finally {
      this._releaseStream();
      this.chunks = [];
    }
  }

  _releaseStream() {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }
}

/** SHA-256 checksum of a Blob, hex-encoded — Section 9. */
export async function sha256Hex(blob) {
  const buffer = await blob.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
